import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Pencil, ArrowLeft, Link as LinkIcon, Trash2, Camera, Upload, FileText, Info, Download, X, Edit, Settings } from 'lucide-react';
import Editor from '@monaco-editor/react';
import AppLayout from '../components/AppLayout';
import RichTextEditor from '../components/RichTextEditor';
import PhotoGallery from '../components/PhotoGallery';
import VideoGallery, { Video, VideoGalleryHandle } from '../components/VideoGallery';
import DocumentGallery from '../components/DocumentGallery';
import ConfirmDialog from '../components/ConfirmDialog';
import SortableList from '../components/SortableList';
import AddItemsModal from '../components/AddItemsModal';
import VideoUploadModal from '../components/VideoUploadModal';
import VideoPlayer from '../components/VideoPlayer';
import AmenityCard from '../components/AmenityCard';
import AmenityFormModal, { AmenityFormData } from '../components/AmenityFormModal';
import AmenityViewModal from '../components/AmenityViewModal';
import S3Image from '../components/S3Image';
import ChangeHistoryButton from '../components/ChangeHistoryButton';
import ChangeHistoryDrawer from '../components/ChangeHistoryDrawer';
import { Project, ProjectAddress } from '../types/project';
import { Amenity } from '../types/amenity';
import { getImageUrl } from '../services/imageService';
import { getAmenities, createAmenity, updateAmenity, deleteAmenity } from '../services/amenityService';
import api from '../services/api';
import { toast } from 'react-toastify';
import '../styles/ProjectDetails.css';
import '../styles/Buttons.css';

type TabType = 'general' | 'features' | 'amenities' | 'documents' | 'media' | 'templates';

const ProjectDetails: React.FC = () => {
  const navigate = useNavigate();
  const { workspaceId, projectId } = useParams<{ workspaceId: string; projectId: string }>();

  const [activeTab, setActiveTab] = useState<TabType>('general');
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState('');
  const [uploadingProjectLogo, setUploadingProjectLogo] = useState(false);
  const [uploadingPortalLogo, setUploadingPortalLogo] = useState(false);
  const [uploadingConstructionLogo, setUploadingConstructionLogo] = useState(false);
  const projectLogoInputRef = useRef<HTMLInputElement>(null);
  const portalLogoInputRef = useRef<HTMLInputElement>(null);
  const constructionLogoInputRef = useRef<HTMLInputElement>(null);

  // Basic Info editing
  const [editingBasicInfo, setEditingBasicInfo] = useState(false);
  const [basicInfoData, setBasicInfoData] = useState({
    name: '',
    projectCode: '',
    ongoingSelectionPhase: '',
    builderVendorNo: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    contactWebsite: '',
    status: '',
    constructionStartDate: '',
    estimatedCompletionDate: ''
  });
  const [savingBasicInfo, setSavingBasicInfo] = useState(false);

  // Project Address editing
  const [editingProjectAddress, setEditingProjectAddress] = useState(false);
  const [projectAddressData, setProjectAddressData] = useState({
    street1: '',
    street2: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    notes: ''
  });
  const [savingProjectAddress, setSavingProjectAddress] = useState(false);

  // Office Address editing
  const [editingOfficeAddress, setEditingOfficeAddress] = useState(false);
  const [officeAddressData, setOfficeAddressData] = useState({
    street1: '',
    street2: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    notes: ''
  });
  const [savingOfficeAddress, setSavingOfficeAddress] = useState(false);

  // Description editing
  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionData, setDescriptionData] = useState('');
  const [savingDescription, setSavingDescription] = useState(false);

  // Intro Video
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);

  // Unit/Lot Information editing
  const [editingUnitInfo, setEditingUnitInfo] = useState(false);
  const [unitInfoData, setUnitInfoData] = useState<any[]>([]);
  const [savingUnitInfo, setSavingUnitInfo] = useState(false);

  // Spaces editing
  const [editingSpaces, setEditingSpaces] = useState(false);
  const [spacesData, setSpacesData] = useState<string[]>([]);
  const [savingSpaces, setSavingSpaces] = useState(false);
  const [showAddSpacesModal, setShowAddSpacesModal] = useState(false);

  // Levels editing
  const [editingLevels, setEditingLevels] = useState(false);
  const [levelsData, setLevelsData] = useState<string[]>([]);
  const [savingLevels, setSavingLevels] = useState(false);
  const [showAddLevelsModal, setShowAddLevelsModal] = useState(false);

  // Views editing
  const [editingViews, setEditingViews] = useState(false);
  const [viewsData, setViewsData] = useState<string[]>([]);
  const [savingViews, setSavingViews] = useState(false);
  const [showAddViewsModal, setShowAddViewsModal] = useState(false);

  // Features description editing
  const [editingFeatures, setEditingFeatures] = useState(false);
  const [featuresData, setFeaturesData] = useState('');
  const [savingFeatures, setSavingFeatures] = useState(false);

  // Amenities
  const [amenities, setAmenities] = useState<Amenity[]>([]);
  const [loadingAmenities, setLoadingAmenities] = useState(false);
  const [amenityFormOpen, setAmenityFormOpen] = useState(false);
  const [amenityViewOpen, setAmenityViewOpen] = useState(false);
  const [selectedAmenity, setSelectedAmenity] = useState<Amenity | null>(null);
  const [editingAmenity, setEditingAmenity] = useState<Amenity | null>(null);

  // Videos
  const [videos, setVideos] = useState<Video[]>([]);
  const videoGalleryRef = React.useRef<VideoGalleryHandle>(null);

  // Delete project
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletingTemplate, setDeletingTemplate] = useState(false);

  // Confirm dialog
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogConfig, setConfirmDialogConfig] = useState<{
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    title: '',
    message: '',
    onConfirm: () => {}
  });

  // Templates
  const [templates, setTemplates] = useState<any[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [showUploadTemplateModal, setShowUploadTemplateModal] = useState(false);
  const [showPlaceholdersModal, setShowPlaceholdersModal] = useState(false);
  const [selectedTemplateType, setSelectedTemplateType] = useState<string>('');
  const [uploadTemplateForm, setUploadTemplateForm] = useState({
    name: '',
    type: '',
    description: '',
    file: null as File | null
  });
  const [uploadingTemplate, setUploadingTemplate] = useState(false);
  const [placeholders, setPlaceholders] = useState<Array<{
    key: string;
    description: string;
    example: string;
  }>>([]);

  // Template Editor
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<any>(null);
  const [templateContent, setTemplateContent] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [loadingTemplateContent, setLoadingTemplateContent] = useState(false);

  // Template Metadata Editor
  const [showTemplateMetadataModal, setShowTemplateMetadataModal] = useState(false);
  const [editingTemplateMetadata, setEditingTemplateMetadata] = useState<any>(null);
  const [templateMetadataForm, setTemplateMetadataForm] = useState({
    name: '',
    type: ''
  });

  const tabs = [
    { id: 'general' as TabType, label: 'General' },
    { id: 'features' as TabType, label: 'Features and Finishes' },
    { id: 'amenities' as TabType, label: 'Amenities' },
    { id: 'documents' as TabType, label: 'Documents' },
    { id: 'media' as TabType, label: 'Media' },
    { id: 'templates' as TabType, label: 'Templates' }
  ];

  useEffect(() => {
    if (workspaceId && projectId) {
      fetchProject();
      fetchVideos();
    }
  }, [workspaceId, projectId]);

  useEffect(() => {
    if (activeTab === 'amenities') {
      fetchAmenities();
    }
    if (activeTab === 'templates') {
      fetchTemplates();
    }
  }, [activeTab]);

  const fetchProject = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/workspaces/${workspaceId}/projects/${projectId}`);
      const projectData: Project = response.data.project;
      setProject(projectData);

      // Fetch logo URL if logoId exists
      if (projectData.logoId && workspaceId) {
        try {
          const url = await getImageUrl(workspaceId, projectData.logoId);
          setLogoUrl(url);
        } catch (err) {
          console.error('Error fetching logo URL:', err);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch project details');
      console.error('Error fetching project:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/workspaces/${workspaceId}/projects`);
  };

  const fetchVideos = async () => {
    try {
      const response = await api.get(`/workspaces/${workspaceId}/project/${projectId}/videos`);
      const backendVideos = response.data.videos || [];
      const mappedVideos: Video[] = backendVideos.map((v: any) => ({
        _id: v._id,
        title: v.title || 'Untitled Video',
        url: v.url,
        type: v.source as 'youtube' | 'vimeo' | 'mp4' | 'other',
        thumbnail: v.thumbnailUrl,
        createdAt: v.createdAt
      }));
      setVideos(mappedVideos);
    } catch (err: any) {
      console.error('Error fetching videos:', err);
    }
  };

  const fetchAmenities = async () => {
    try {
      setLoadingAmenities(true);
      const data = await getAmenities(workspaceId!, projectId!);
      setAmenities(data);
    } catch (err: any) {
      console.error('Error fetching amenities:', err);
      toast.error('Failed to load amenities');
    } finally {
      setLoadingAmenities(false);
    }
  };

  const handleAddVideo = async (video: Omit<Video, '_id' | 'createdAt'>) => {
    try {
      const response = await api.post(`/workspaces/${workspaceId}/project/${projectId}/videos`, video);
      const backendVideo = response.data.video;
      const mappedVideo: Video = {
        _id: backendVideo._id,
        title: backendVideo.title || 'Untitled Video',
        url: backendVideo.url,
        type: backendVideo.source as 'youtube' | 'vimeo' | 'mp4' | 'other',
        thumbnail: backendVideo.thumbnailUrl,
        createdAt: backendVideo.createdAt
      };
      setVideos(prev => [...prev, mappedVideo]);
    } catch (err: any) {
      console.error('Error adding video:', err);
      throw err;
    }
  };

  const handleDeleteVideo = async (videoId: string) => {
    try {
      await api.delete(`/workspaces/${workspaceId}/videos/${videoId}`);
      setVideos(prev => prev.filter(v => v._id !== videoId));
    } catch (err: any) {
      console.error('Error deleting video:', err);
      throw err;
    }
  };

  const handleUpdateVideo = async (videoId: string, title: string) => {
    try {
      await api.put(`/workspaces/${workspaceId}/videos/${videoId}`, { title });
      setVideos(prev => prev.map(v =>
        v._id === videoId ? { ...v, title } : v
      ));
    } catch (err: any) {
      console.error('Error updating video:', err);
      throw err;
    }
  };

  // Generic logo upload handler
  const handleLogoUpload = async (
    file: File,
    logoFieldName: 'logoId' | 'portalHomeLogoId' | 'constructionLogoId',
    logoType: string,
    imageType: 'logo' | 'portal_home_logo' | 'construction_logo',
    setUploadingState: (uploading: boolean) => void
  ) => {
    if (!file || !workspaceId || !projectId) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      toast.error('Image size must be less than 5MB');
      return;
    }

    try {
      setUploadingState(true);
      setError('');

      const formData = new FormData();
      formData.append('file', file);
      formData.append('entityType', 'project');
      formData.append('entityId', projectId);
      formData.append('imageType', imageType);

      const uploadResponse = await api.post(
        `/workspaces/${workspaceId}/upload/image`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      const imageId = uploadResponse.data.image._id;

      await api.put(`/workspaces/${workspaceId}/projects/${projectId}`, {
        [logoFieldName]: imageId,
      });

      await fetchProject();
      toast.success(`${logoType} updated successfully`);
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to upload ${logoType.toLowerCase()}`);
      console.error(`Error uploading ${logoType}:`, err);
      toast.error(`Failed to upload ${logoType.toLowerCase()}`);
    } finally {
      setUploadingState(false);
    }
  };

  // Project Logo handlers
  const handleProjectLogoClick = () => {
    projectLogoInputRef.current?.click();
  };

  const handleProjectLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      await handleLogoUpload(file, 'logoId', 'Project Logo', 'logo', setUploadingProjectLogo);
      if (projectLogoInputRef.current) {
        projectLogoInputRef.current.value = '';
      }
    }
  };

  // Portal Home Logo handlers
  const handlePortalLogoClick = () => {
    portalLogoInputRef.current?.click();
  };

  const handlePortalLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      await handleLogoUpload(file, 'portalHomeLogoId', 'Purchaser Portal Logo', 'portal_home_logo', setUploadingPortalLogo);
      if (portalLogoInputRef.current) {
        portalLogoInputRef.current.value = '';
      }
    }
  };

  // Construction Company Logo handlers
  const handleConstructionLogoClick = () => {
    constructionLogoInputRef.current?.click();
  };

  const handleConstructionLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      await handleLogoUpload(file, 'constructionLogoId', 'Construction Company Logo', 'construction_logo', setUploadingConstructionLogo);
      if (constructionLogoInputRef.current) {
        constructionLogoInputRef.current.value = '';
      }
    }
  };

  const handleDeleteProject = async () => {
    if (!workspaceId || !projectId) return;

    try {
      setDeleting(true);
      await api.delete(`/workspaces/${workspaceId}/projects/${projectId}`);
      toast.success('Project deleted successfully');
      navigate(`/workspaces/${workspaceId}/projects`);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to delete project');
      console.error('Error deleting project:', err);
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  // Basic Info handlers
  const handleEditBasicInfo = () => {
    if (!project) return;
    setBasicInfoData({
      name: project.name || '',
      projectCode: project.projectCode || '',
      ongoingSelectionPhase: project.ongoingSelectionPhase || '',
      builderVendorNo: project.builderVendorNo || '',
      contactName: project.contactName || '',
      contactEmail: project.contactEmail || '',
      contactPhone: project.contactPhone || '',
      contactWebsite: project.contactWebsite || '',
      status: project.status || '',
      constructionStartDate: project.constructionStartDate
        ? project.constructionStartDate.split('T')[0]
        : '',
      estimatedCompletionDate: project.estimatedCompletionDate
        ? project.estimatedCompletionDate.split('T')[0]
        : ''
    });
    setEditingBasicInfo(true);
  };

  const handleCancelBasicInfo = () => {
    setEditingBasicInfo(false);
  };

  const handleBasicInfoChange = (field: string, value: string) => {
    setBasicInfoData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveBasicInfo = async () => {
    if (!project) return;
    try {
      setSavingBasicInfo(true);
      const response = await api.put(
        `/workspaces/${workspaceId}/projects/${projectId}`,
        {
          name: basicInfoData.name.trim(),
          projectCode: basicInfoData.projectCode.trim() || undefined,
          ongoingSelectionPhase: basicInfoData.ongoingSelectionPhase.trim() || undefined,
          builderVendorNo: basicInfoData.builderVendorNo.trim() || undefined,
          contactName: basicInfoData.contactName.trim() || undefined,
          contactEmail: basicInfoData.contactEmail.trim() || undefined,
          contactPhone: basicInfoData.contactPhone.trim() || undefined,
          contactWebsite: basicInfoData.contactWebsite.trim() || undefined,
          status: basicInfoData.status.trim() || undefined,
          constructionStartDate: basicInfoData.constructionStartDate || undefined,
          estimatedCompletionDate: basicInfoData.estimatedCompletionDate || undefined
        }
      );
      setProject(response.data.project);
      setEditingBasicInfo(false);
      toast.success('Basic information updated successfully');
    } catch (err: any) {
      console.error('Error updating basic info:', err);
      toast.error(err.response?.data?.message || 'Failed to update basic information');
    } finally {
      setSavingBasicInfo(false);
    }
  };

  // Project Address handlers
  const handleEditProjectAddress = () => {
    if (!project) return;
    setProjectAddressData({
      street1: project.projectAddress?.street1 || '',
      street2: project.projectAddress?.street2 || '',
      city: project.projectAddress?.city || '',
      state: project.projectAddress?.state || '',
      zip: project.projectAddress?.zip || '',
      country: project.projectAddress?.country || 'Canada',
      notes: project.projectAddress?.notes || ''
    });
    setEditingProjectAddress(true);
  };

  const handleCancelProjectAddress = () => {
    setEditingProjectAddress(false);
  };

  const handleProjectAddressChange = (field: string, value: string) => {
    setProjectAddressData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveProjectAddress = async () => {
    if (!project) return;
    try {
      setSavingProjectAddress(true);
      const response = await api.put(
        `/workspaces/${workspaceId}/projects/${projectId}`,
        { projectAddress: projectAddressData }
      );
      setProject(response.data.project);
      setEditingProjectAddress(false);
      toast.success('Project address updated successfully');
    } catch (err: any) {
      console.error('Error updating project address:', err);
      toast.error(err.response?.data?.message || 'Failed to update project address');
    } finally {
      setSavingProjectAddress(false);
    }
  };

  // Office Address handlers
  const handleEditOfficeAddress = () => {
    if (!project) return;
    setOfficeAddressData({
      street1: project.officeAddress?.street1 || '',
      street2: project.officeAddress?.street2 || '',
      city: project.officeAddress?.city || '',
      state: project.officeAddress?.state || '',
      zip: project.officeAddress?.zip || '',
      country: project.officeAddress?.country || 'Canada',
      notes: project.officeAddress?.notes || ''
    });
    setEditingOfficeAddress(true);
  };

  const handleCancelOfficeAddress = () => {
    setEditingOfficeAddress(false);
  };

  const handleOfficeAddressChange = (field: string, value: string) => {
    setOfficeAddressData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveOfficeAddress = async () => {
    if (!project) return;
    try {
      setSavingOfficeAddress(true);
      const response = await api.put(
        `/workspaces/${workspaceId}/projects/${projectId}`,
        { officeAddress: officeAddressData }
      );
      setProject(response.data.project);
      setEditingOfficeAddress(false);
      toast.success('Office address updated successfully');
    } catch (err: any) {
      console.error('Error updating office address:', err);
      toast.error(err.response?.data?.message || 'Failed to update office address');
    } finally {
      setSavingOfficeAddress(false);
    }
  };

  // Description handlers
  const handleEditDescription = () => {
    if (!project) return;
    setDescriptionData(project.description || '');
    setEditingDescription(true);
  };

  const handleCancelDescription = () => {
    setEditingDescription(false);
  };

  const handleSaveDescription = async () => {
    if (!project) return;
    try {
      setSavingDescription(true);
      const response = await api.put(
        `/workspaces/${workspaceId}/projects/${projectId}`,
        { description: descriptionData || undefined }
      );
      setProject(response.data.project);
      setEditingDescription(false);
      toast.success('Description updated successfully');
    } catch (err: any) {
      console.error('Error updating description:', err);
      toast.error(err.response?.data?.message || 'Failed to update description');
    } finally {
      setSavingDescription(false);
    }
  };

  // Intro Video handlers
  const handleVideoUploaded = async (videoUrl: string, videoType: 'youtube' | 'vimeo' | 'mp4') => {
    if (!project) return;
    try {
      setUploadingVideo(true);
      console.log('Updating intro video:', { videoUrl, videoType });
      const response = await api.put(
        `/workspaces/${workspaceId}/projects/${projectId}`,
        {
          introVideoUrl: videoUrl,
          introVideoType: videoType
        }
      );
      console.log('Video update response:', response.data);
      setProject(response.data.project);
      toast.success('Intro video added successfully');
    } catch (err: any) {
      console.error('Error updating intro video:', err);
      console.error('Error details:', err.response?.data);
      toast.error(err.response?.data?.message || 'Failed to update intro video');
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleRemoveVideo = async () => {
    if (!project) return;
    try {
      setUploadingVideo(true);
      const response = await api.put(
        `/workspaces/${workspaceId}/projects/${projectId}`,
        {
          introVideoUrl: null,
          introVideoType: null
        }
      );
      setProject(response.data.project);
      toast.success('Intro video removed successfully');
    } catch (err: any) {
      console.error('Error removing intro video:', err);
      toast.error(err.response?.data?.message || 'Failed to remove intro video');
    } finally {
      setUploadingVideo(false);
    }
  };

  // Unit Info handlers
  const handleEditUnitInfo = () => {
    if (!project) return;
    // If no additionalInfo exists, initialize with all property types
    if (!project.additionalInfo || project.additionalInfo.length === 0) {
      setUnitInfoData([
        { type: 'condominium', totalUnits: undefined, totalFloors: undefined },
        { type: 'detached', totalUnits: undefined },
        { type: 'semi-detached', totalUnits: undefined, totalHomes: undefined },
        { type: 'townhouse', totalBuildings: undefined, totalHomes: undefined }
      ]);
    } else {
      // Ensure all types exist, merge with existing data
      const types: ('condominium' | 'detached' | 'semi-detached' | 'townhouse')[] = ['condominium', 'detached', 'semi-detached', 'townhouse'];
      const existingTypes = project.additionalInfo.map(info => info.type);
      const allData = [
        ...project.additionalInfo,
        ...types
          .filter(type => !existingTypes.includes(type))
          .map(type => {
            if (type === 'condominium') return { type, totalUnits: undefined, totalFloors: undefined };
            if (type === 'detached') return { type, totalUnits: undefined };
            if (type === 'semi-detached') return { type, totalUnits: undefined, totalHomes: undefined };
            return { type: 'townhouse' as const, totalBuildings: undefined, totalHomes: undefined };
          })
      ];
      // Sort to maintain consistent order
      const sortOrder = { 'condominium': 0, 'detached': 1, 'semi-detached': 2, 'townhouse': 3 };
      allData.sort((a, b) => sortOrder[a.type] - sortOrder[b.type]);
      setUnitInfoData(allData);
    }
    setEditingUnitInfo(true);
  };

  const handleCancelUnitInfo = () => {
    setEditingUnitInfo(false);
  };

  const handleSaveUnitInfo = async () => {
    if (!project) return;
    try {
      setSavingUnitInfo(true);
      const response = await api.put(
        `/workspaces/${workspaceId}/projects/${projectId}`,
        { additionalInfo: unitInfoData }
      );
      setProject(response.data.project);
      setEditingUnitInfo(false);
      toast.success('Unit information updated successfully');
    } catch (err: any) {
      console.error('Error updating unit info:', err);
      toast.error(err.response?.data?.message || 'Failed to update unit information');
    } finally {
      setSavingUnitInfo(false);
    }
  };

  // Spaces handlers
  const handleEditSpaces = () => {
    if (!project) return;
    setSpacesData(project.spaces || []);
    setEditingSpaces(true);
  };

  const handleCancelSpaces = () => {
    setEditingSpaces(false);
  };

  const handleSpacesChange = (newSpaces: string[]) => {
    setSpacesData(newSpaces);
  };

  const handleAddSpacesFromModal = (newSpaces: string[]) => {
    setSpacesData(prev => [...prev, ...newSpaces]);
  };

  const handleSaveSpaces = async () => {
    if (!project) return;
    try {
      setSavingSpaces(true);
      const response = await api.put(
        `/workspaces/${workspaceId}/projects/${projectId}`,
        { spaces: spacesData }
      );
      setProject(response.data.project);
      setEditingSpaces(false);
      toast.success('Spaces updated successfully');
    } catch (err: any) {
      console.error('Error updating spaces:', err);
      toast.error(err.response?.data?.message || 'Failed to update spaces');
    } finally {
      setSavingSpaces(false);
    }
  };

  // Levels handlers
  const handleEditLevels = () => {
    if (!project) return;
    setLevelsData(project.levels || []);
    setEditingLevels(true);
  };

  const handleCancelLevels = () => {
    setEditingLevels(false);
  };

  const handleLevelsChange = (newLevels: string[]) => {
    setLevelsData(newLevels);
  };

  const handleAddLevelsFromModal = (newLevels: string[]) => {
    setLevelsData(prev => [...prev, ...newLevels]);
  };

  const handleSaveLevels = async () => {
    if (!project) return;
    try {
      setSavingLevels(true);
      const response = await api.put(
        `/workspaces/${workspaceId}/projects/${projectId}`,
        { levels: levelsData }
      );
      setProject(response.data.project);
      setEditingLevels(false);
      toast.success('Levels updated successfully');
    } catch (err: any) {
      console.error('Error updating levels:', err);
      toast.error(err.response?.data?.message || 'Failed to update levels');
    } finally {
      setSavingLevels(false);
    }
  };

  // Views handlers
  const handleEditViews = () => {
    if (!project) return;
    setViewsData(project.views || []);
    setEditingViews(true);
  };

  const handleCancelViews = () => {
    setEditingViews(false);
  };

  const handleViewsChange = (newViews: string[]) => {
    setViewsData(newViews);
  };

  const handleAddViewsFromModal = (newViews: string[]) => {
    setViewsData(prev => [...prev, ...newViews]);
  };

  const handleSaveViews = async () => {
    if (!project) return;
    try {
      setSavingViews(true);
      const response = await api.put(
        `/workspaces/${workspaceId}/projects/${projectId}`,
        { views: viewsData }
      );
      setProject(response.data.project);
      setEditingViews(false);
      toast.success('Views updated successfully');
    } catch (err: any) {
      console.error('Error updating views:', err);
      toast.error(err.response?.data?.message || 'Failed to update views');
    } finally {
      setSavingViews(false);
    }
  };

  // Features handlers
  const handleEditFeatures = () => {
    if (!project) return;
    setFeaturesData(project.features || '');
    setEditingFeatures(true);
  };

  const handleCancelFeatures = () => {
    setEditingFeatures(false);
  };

  const handleSaveFeatures = async () => {
    if (!project) return;
    try {
      setSavingFeatures(true);
      const response = await api.put(
        `/workspaces/${workspaceId}/projects/${projectId}`,
        { features: featuresData || undefined }
      );
      setProject(response.data.project);
      setEditingFeatures(false);
      toast.success('Features updated successfully');
    } catch (err: any) {
      console.error('Error updating features:', err);
      toast.error(err.response?.data?.message || 'Failed to update features');
    } finally {
      setSavingFeatures(false);
    }
  };

  // Amenity handlers
  const handleCreateAmenity = () => {
    setEditingAmenity(null);
    setAmenityFormOpen(true);
  };

  const handleEditAmenity = (amenity: Amenity) => {
    setEditingAmenity(amenity);
    setAmenityFormOpen(true);
    setAmenityViewOpen(false);
  };

  const handleViewAmenity = (amenity: Amenity) => {
    setSelectedAmenity(amenity);
    setAmenityViewOpen(true);
  };

  const handleSaveAmenity = async (data: AmenityFormData) => {
    try {
      if (editingAmenity) {
        await updateAmenity(workspaceId!, projectId!, editingAmenity._id, data);
        toast.success('Amenity updated successfully');
      } else {
        await createAmenity(workspaceId!, projectId!, data);
        toast.success('Amenity created successfully');
      }
      setAmenityFormOpen(false);
      setEditingAmenity(null);
      fetchAmenities();
    } catch (err: any) {
      console.error('Error saving amenity:', err);
      toast.error(err.response?.data?.message || 'Failed to save amenity');
    }
  };

  const handleDeleteAmenity = async (amenity: Amenity) => {
    setConfirmDialogConfig({
      title: 'Delete Amenity',
      message: 'Are you sure you want to delete this amenity? This action cannot be undone.',
      onConfirm: async () => {
        try {
          await deleteAmenity(workspaceId!, projectId!, amenity._id);
          toast.success('Amenity deleted successfully');
          setConfirmDialogOpen(false);
          setAmenityViewOpen(false);
          fetchAmenities();
        } catch (err: any) {
          console.error('Error deleting amenity:', err);
          toast.error(err.response?.data?.message || 'Failed to delete amenity');
        }
      }
    });
    setConfirmDialogOpen(true);
  };

  // Template handlers
  const templateTypes = [
    { value: 'color_selection_amendment', label: 'Color Selection Amendment' },
    { value: 'design_studio_amendment', label: 'Design Studio Amendment' },
    { value: 'revised_design_studio_amendment', label: 'Revised Design Studio Amendment' },
    { value: 'design_studio_invoice', label: 'Design Studio Invoice' },
    { value: 'design_studio_summary_invoice', label: 'Design Studio Summary Invoice' },
    { value: 'parking_selection_amendment', label: 'Parking Selection Amendment' },
    { value: 'parking_invoice', label: 'Parking Invoice' },
    { value: 'locker_selection_amendment', label: 'Locker Selection Amendment' },
    { value: 'locker_invoice', label: 'Locker Invoice' },
    { value: 'bicycle_selection_amendment', label: 'Bicycle Selection Amendment' },
    { value: 'bicycle_invoice', label: 'Bicycle Invoice' }
  ];

  const fetchTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const response = await api.get(`/templates/${workspaceId}/project/${projectId}`);
      setTemplates(response.data.data);
    } catch (err: any) {
      console.error('Error fetching templates:', err);
      toast.error('Failed to load templates');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const fetchPlaceholders = async (type: string) => {
    try {
      const response = await api.get(`/templates/placeholders/${type}`);
      setPlaceholders(response.data.data);
      setSelectedTemplateType(type);
      setShowPlaceholdersModal(true);
    } catch (err: any) {
      console.error('Error fetching placeholders:', err);
      toast.error('Failed to load placeholders');
    }
  };

  const handleTemplateFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.html')) {
        toast.error('Please select an HTML template file (.html)');
        return;
      }
      setUploadTemplateForm({ ...uploadTemplateForm, file });
    }
  };

  const handleUploadTemplate = async () => {
    if (!uploadTemplateForm.name || !uploadTemplateForm.type || !uploadTemplateForm.file) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setUploadingTemplate(true);
      const formData = new FormData();
      formData.append('file', uploadTemplateForm.file);
      formData.append('name', uploadTemplateForm.name);
      formData.append('type', uploadTemplateForm.type);
      formData.append('description', uploadTemplateForm.description || '');
      formData.append('projectId', projectId!);
      formData.append('workspaceId', workspaceId!);

      await api.post('/templates/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Template uploaded successfully');
      setShowUploadTemplateModal(false);
      setUploadTemplateForm({ name: '', type: '', description: '', file: null });
      fetchTemplates();
    } catch (err: any) {
      console.error('Error uploading template:', err);
      toast.error(err.response?.data?.message || 'Failed to upload template');
    } finally {
      setUploadingTemplate(false);
    }
  };

  const handleDownloadTemplate = async (template: any) => {
    try {
      const response = await api.get(`/templates/${workspaceId}/${template._id}/download`, {
        responseType: 'blob'
      });

      // Create blob URL and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', template.fileName || 'template.html');
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Template downloaded successfully');
    } catch (err: any) {
      console.error('Error downloading template:', err);
      toast.error('Failed to download template');
    }
  };

  const handleEditTemplate = async (template: any) => {
    try {
      setEditingTemplate(template);
      setShowTemplateEditor(true);
      setLoadingTemplateContent(true);
      setTemplateContent('');

      console.log('[handleEditTemplate] Fetching template:', template._id);

      // Fetch template content
      const response = await api.get(`/templates/${workspaceId}/${template._id}/download`, {
        responseType: 'blob'
      });

      console.log('[handleEditTemplate] Response received, blob size:', response.data.size);

      // Convert blob to text using FileReader for better browser compatibility
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        console.log('[handleEditTemplate] Template loaded, length:', text.length);
        setTemplateContent(text);
        setLoadingTemplateContent(false);
      };
      reader.onerror = (e) => {
        console.error('[handleEditTemplate] FileReader error:', e);
        toast.error('Failed to read template content');
        setShowTemplateEditor(false);
        setEditingTemplate(null);
        setLoadingTemplateContent(false);
      };
      reader.readAsText(response.data);
    } catch (err: any) {
      console.error('[handleEditTemplate] Error loading template:', err);
      toast.error('Failed to load template content');
      setShowTemplateEditor(false);
      setEditingTemplate(null);
      setLoadingTemplateContent(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!editingTemplate || !templateContent) {
      toast.error('No template content to save');
      return;
    }

    try {
      setSavingTemplate(true);

      // Convert content to blob
      const blob = new Blob([templateContent], { type: 'text/html' });
      const file = new File([blob], editingTemplate.fileName, { type: 'text/html' });

      // Create form data
      const formData = new FormData();
      formData.append('file', file);

      // Upload using replace endpoint
      await api.post(`/templates/${workspaceId}/${editingTemplate._id}/replace`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Template saved successfully');
      setShowTemplateEditor(false);
      setEditingTemplate(null);
      setTemplateContent('');
      fetchTemplates();
    } catch (err: any) {
      console.error('Error saving template:', err);
      toast.error('Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleEditTemplateMetadata = (template: any) => {
    console.log('Editing template metadata:', template);
    setEditingTemplateMetadata(template);
    setTemplateMetadataForm({
      name: template.name,
      type: template.type || ''
    });
    setShowTemplateMetadataModal(true);
  };

  const handleSaveTemplateMetadata = async () => {
    if (!editingTemplateMetadata) {
      toast.error('No template selected');
      return;
    }

    if (!templateMetadataForm.name.trim()) {
      toast.error('Template name is required');
      return;
    }

    if (!templateMetadataForm.type) {
      toast.error('Template type is required');
      return;
    }

    try {
      await api.put(`/templates/${workspaceId}/${editingTemplateMetadata._id}`, {
        name: templateMetadataForm.name.trim(),
        type: templateMetadataForm.type
      });

      toast.success('Template updated successfully');
      setShowTemplateMetadataModal(false);
      setEditingTemplateMetadata(null);
      setTemplateMetadataForm({ name: '', type: '' });
      fetchTemplates();
    } catch (err: any) {
      console.error('Error updating template:', err);
      toast.error(err.response?.data?.message || 'Failed to update template');
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    setConfirmDialogConfig({
      title: 'Delete Template',
      message: 'Are you sure you want to delete this template? This action cannot be undone.',
      onConfirm: async () => {
        try {
          setDeletingTemplate(true);
          await api.delete(`/templates/${workspaceId}/${templateId}`);
          toast.success('Template deleted successfully');
          setConfirmDialogOpen(false);
          fetchTemplates();
        } catch (err: any) {
          console.error('Error deleting template:', err);
          toast.error('Failed to delete template');
        } finally {
          setDeletingTemplate(false);
          setConfirmDialogOpen(false);
        }
      }
    });
    setConfirmDialogOpen(true);
  };

  const getTemplateTypeLabel = (type: string) => {
    return templateTypes.find(t => t.value === type)?.label || type;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatAddress = (addr?: ProjectAddress) => {
    if (!addr) return 'No address provided';
    const parts = [addr.street1];
    if (addr.street2) parts.push(addr.street2);
    parts.push(addr.city);
    parts.push(addr.state);
    parts.push(addr.zip);
    parts.push(addr.country);
    return parts.filter(p => p).join(', ');
  };

  if (loading) {
    return (
      <AppLayout onBack={handleBack}>
        <div className="project-details-loading">
          <div className="projects-loading-spinner" />
        </div>
      </AppLayout>
    );
  }

  if (error || !project) {
    return (
      <AppLayout onBack={handleBack}>
        <div className="project-details-error">
          <p>{error || 'Project not found'}</p>
          <button className="btn btn-primary btn-md" onClick={handleBack}>
            Back to Projects
          </button>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout onBack={handleBack}>
      <div className="project-details-container">
        {/* Header Section with Background and Tabs */}
        <div className="project-details-header-section">
          <div className="project-details-header-top">
            <button className="project-details-back-btn" onClick={handleBack}>
              <ArrowLeft size={24} />
            </button>
            <div className="project-details-header-info">
              <h1>{project.name}</h1>
              <p>{formatAddress(project.projectAddress)}</p>
            </div>
            <button
              className="project-details-delete-btn"
              onClick={() => setShowDeleteDialog(true)}
              title="Delete project"
            >
              <Trash2 size={20} />
            </button>
          </div>

          {/* Tabs */}
          <div className="project-details-tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`project-details-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="project-details-tab-content">
          {/* GENERAL TAB */}
          {activeTab === 'general' && (
            <div className="project-details-general">
              {/* Three Logo Cards Row */}
              <div className="project-details-row project-details-logos-row">
                {/* Project Logo */}
                <div className="project-details-card project-details-logo-card">
                  <div className="project-details-logo-display">
                    {uploadingProjectLogo ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '180px' }}>
                        <div className="projects-loading-spinner" />
                      </div>
                    ) : project.logoId && workspaceId ? (
                      <S3Image
                        workspaceId={workspaceId}
                        imageId={project.logoId}
                        alt={project.name}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '180px',
                          objectFit: 'contain'
                        }}
                      />
                    ) : (
                      <div className="project-details-logo-placeholder-box">
                        {project.name.substring(0, 2).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <button
                    className="project-details-logo-edit-overlay"
                    onClick={handleProjectLogoClick}
                    disabled={uploadingProjectLogo}
                  >
                    <Pencil size={20} />
                  </button>
                  <input
                    ref={projectLogoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleProjectLogoUpload}
                    style={{ display: 'none' }}
                  />
                  <div className="project-details-logo-label">Project Logo</div>
                </div>

                {/* Purchaser Portal Logo */}
                <div className="project-details-card project-details-logo-card">
                  <div className="project-details-logo-display">
                    {uploadingPortalLogo ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '180px' }}>
                        <div className="projects-loading-spinner" />
                      </div>
                    ) : project.portalHomeLogoId && workspaceId ? (
                      <S3Image
                        workspaceId={workspaceId}
                        imageId={project.portalHomeLogoId}
                        alt="Purchaser Portal Logo"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '180px',
                          objectFit: 'contain'
                        }}
                      />
                    ) : (
                      <div className="project-details-logo-placeholder-box">
                        PP
                      </div>
                    )}
                  </div>
                  <button
                    className="project-details-logo-edit-overlay"
                    onClick={handlePortalLogoClick}
                    disabled={uploadingPortalLogo}
                  >
                    <Pencil size={20} />
                  </button>
                  <input
                    ref={portalLogoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePortalLogoUpload}
                    style={{ display: 'none' }}
                  />
                  <div className="project-details-logo-label">Purchaser Portal Logo</div>
                </div>

                {/* Construction Company Logo */}
                <div className="project-details-card project-details-logo-card">
                  <div className="project-details-logo-display">
                    {uploadingConstructionLogo ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '180px' }}>
                        <div className="projects-loading-spinner" />
                      </div>
                    ) : project.constructionLogoId && workspaceId ? (
                      <S3Image
                        workspaceId={workspaceId}
                        imageId={project.constructionLogoId}
                        alt="Construction Company Logo"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '180px',
                          objectFit: 'contain'
                        }}
                      />
                    ) : (
                      <div className="project-details-logo-placeholder-box">
                        CC
                      </div>
                    )}
                  </div>
                  <button
                    className="project-details-logo-edit-overlay"
                    onClick={handleConstructionLogoClick}
                    disabled={uploadingConstructionLogo}
                  >
                    <Pencil size={20} />
                  </button>
                  <input
                    ref={constructionLogoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleConstructionLogoUpload}
                    style={{ display: 'none' }}
                  />
                  <div className="project-details-logo-label">Construction Company Logo</div>
                </div>
              </div>

              {/* Row for Basic Info and Addresses */}
              <div className="project-details-row">
                {/* Basic Information */}
                <div className="project-details-card">
                  <div className="project-details-card-header">
                    <h2>Basic Information</h2>
                    {editingBasicInfo ? (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button className="btn btn-secondary btn-sm" onClick={handleCancelBasicInfo}>
                          Cancel
                        </button>
                        <button className="btn btn-primary btn-sm" onClick={handleSaveBasicInfo} disabled={savingBasicInfo}>
                          {savingBasicInfo ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    ) : (
                      <button className="project-details-edit-btn" onClick={handleEditBasicInfo}>
                        <Pencil size={16} />
                      </button>
                    )}
                  </div>

                  {editingBasicInfo ? (
                    <div className="project-details-edit-form">
                      <div className="project-details-form-row">
                        <div className="project-details-form-group">
                          <label>PROJECT NAME:</label>
                          <input
                            type="text"
                            className="form-input"
                            value={basicInfoData.name}
                            onChange={(e) => handleBasicInfoChange('name', e.target.value)}
                          />
                        </div>
                        <div className="project-details-form-group">
                          <label>PROJECT CODE:</label>
                          <input
                            type="text"
                            className="form-input"
                            value={basicInfoData.projectCode}
                            onChange={(e) => handleBasicInfoChange('projectCode', e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="project-details-form-row">
                        <div className="project-details-form-group">
                          <label>SELECTION PHASE:</label>
                          <select
                            className="form-input"
                            value={basicInfoData.ongoingSelectionPhase}
                            onChange={(e) => handleBasicInfoChange('ongoingSelectionPhase', e.target.value)}
                          >
                            <option value="">Select Phase</option>
                            <option value="One">One</option>
                            <option value="Two">Two</option>
                            <option value="Three">Three</option>
                            <option value="Four">Four</option>
                            <option value="Five">Five</option>
                          </select>
                        </div>
                        <div className="project-details-form-group">
                          <label>STATUS:</label>
                          <input
                            type="text"
                            className="form-input"
                            value={basicInfoData.status}
                            onChange={(e) => handleBasicInfoChange('status', e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="project-details-form-row">
                        <div className="project-details-form-group">
                          <label>CONTACT NAME:</label>
                          <input
                            type="text"
                            className="form-input"
                            value={basicInfoData.contactName}
                            onChange={(e) => handleBasicInfoChange('contactName', e.target.value)}
                          />
                        </div>
                        <div className="project-details-form-group">
                          <label>CONTACT EMAIL:</label>
                          <input
                            type="email"
                            className="form-input"
                            value={basicInfoData.contactEmail}
                            onChange={(e) => handleBasicInfoChange('contactEmail', e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="project-details-form-row">
                        <div className="project-details-form-group">
                          <label>CONTACT PHONE:</label>
                          <input
                            type="tel"
                            className="form-input"
                            value={basicInfoData.contactPhone}
                            onChange={(e) => handleBasicInfoChange('contactPhone', e.target.value)}
                          />
                        </div>
                        <div className="project-details-form-group">
                          <label>WEBSITE:</label>
                          <input
                            type="url"
                            className="form-input"
                            value={basicInfoData.contactWebsite}
                            onChange={(e) => handleBasicInfoChange('contactWebsite', e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="project-details-form-row">
                        <div className="project-details-form-group">
                          <label>CONSTRUCTION START:</label>
                          <input
                            type="date"
                            className="form-input"
                            value={basicInfoData.constructionStartDate}
                            onChange={(e) => handleBasicInfoChange('constructionStartDate', e.target.value)}
                          />
                        </div>
                        <div className="project-details-form-group">
                          <label>ESTIMATED COMPLETION:</label>
                          <input
                            type="date"
                            className="form-input"
                            value={basicInfoData.estimatedCompletionDate}
                            onChange={(e) => handleBasicInfoChange('estimatedCompletionDate', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="project-details-grid">
                      <div className="project-details-field">
                        <label>PROJECT NAME:</label>
                        <span>{project.name}</span>
                      </div>
                      <div className="project-details-field">
                        <label>PROJECT CODE:</label>
                        <span>{project.projectCode || '—'}</span>
                      </div>
                      <div className="project-details-field">
                        <label>BUILDER:</label>
                        <span>{project.builderName || '—'}</span>
                      </div>
                      <div className="project-details-field">
                        <label>SELECTION PHASE:</label>
                        <span>{project.ongoingSelectionPhase || '—'}</span>
                      </div>
                      <div className="project-details-field">
                        <label>STATUS:</label>
                        <span>{project.status || '—'}</span>
                      </div>
                      <div className="project-details-field">
                        <label>CONTACT NAME:</label>
                        <span>{project.contactName || '—'}</span>
                      </div>
                      <div className="project-details-field">
                        <label>CONTACT EMAIL:</label>
                        <span>{project.contactEmail || '—'}</span>
                      </div>
                      <div className="project-details-field">
                        <label>CONTACT PHONE:</label>
                        <span>{project.contactPhone || '—'}</span>
                      </div>
                      <div className="project-details-field">
                        <label>WEBSITE:</label>
                        <span>
                          {project.contactWebsite ? (
                            <a href={project.contactWebsite} target="_blank" rel="noopener noreferrer">
                              {project.contactWebsite}
                            </a>
                          ) : (
                            '—'
                          )}
                        </span>
                      </div>
                      <div className="project-details-field">
                        <label>CONSTRUCTION START:</label>
                        <span>
                          {project.constructionStartDate
                            ? new Date(project.constructionStartDate).toLocaleDateString()
                            : '—'}
                        </span>
                      </div>
                      <div className="project-details-field">
                        <label>ESTIMATED COMPLETION:</label>
                        <span>
                          {project.estimatedCompletionDate
                            ? new Date(project.estimatedCompletionDate).toLocaleDateString()
                            : '—'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Combined Addresses Card */}
                <div className="project-details-card">
                  <div className="project-details-card-header">
                    <h2>Addresses</h2>
                  </div>

                  {/* Project Address Section */}
                  <div style={{ marginBottom: '32px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                      <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#48182F', margin: 0 }}>Project Address</h3>
                      {editingProjectAddress ? (
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button className="btn btn-secondary btn-sm" onClick={handleCancelProjectAddress}>
                            Cancel
                          </button>
                          <button className="btn btn-primary btn-sm" onClick={handleSaveProjectAddress} disabled={savingProjectAddress}>
                            {savingProjectAddress ? 'Saving...' : 'Save'}
                          </button>
                        </div>
                      ) : (
                        <button className="project-details-edit-btn" onClick={handleEditProjectAddress}>
                          <Pencil size={16} />
                        </button>
                      )}
                    </div>

                    {editingProjectAddress ? (
                      <div className="project-details-edit-form">
                        <div className="project-details-form-group-full">
                          <label>STREET ADDRESS 1:</label>
                          <input
                            type="text"
                            className="form-input"
                            value={projectAddressData.street1}
                            onChange={(e) => handleProjectAddressChange('street1', e.target.value)}
                          />
                        </div>
                        <div className="project-details-form-group-full">
                          <label>STREET ADDRESS 2:</label>
                          <input
                            type="text"
                            className="form-input"
                            value={projectAddressData.street2}
                            onChange={(e) => handleProjectAddressChange('street2', e.target.value)}
                          />
                        </div>
                        <div className="project-details-form-row">
                          <div className="project-details-form-group">
                            <label>CITY:</label>
                            <input
                              type="text"
                              className="form-input"
                              value={projectAddressData.city}
                              onChange={(e) => handleProjectAddressChange('city', e.target.value)}
                            />
                          </div>
                          <div className="project-details-form-group">
                            <label>STATE/PROVINCE:</label>
                            <input
                              type="text"
                              className="form-input"
                              value={projectAddressData.state}
                              onChange={(e) => handleProjectAddressChange('state', e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="project-details-form-row">
                          <div className="project-details-form-group">
                            <label>ZIP/POSTAL CODE:</label>
                            <input
                              type="text"
                              className="form-input"
                              value={projectAddressData.zip}
                              onChange={(e) => handleProjectAddressChange('zip', e.target.value)}
                            />
                          </div>
                          <div className="project-details-form-group">
                            <label>COUNTRY:</label>
                            <input
                              type="text"
                              className="form-input"
                              value={projectAddressData.country}
                              onChange={(e) => handleProjectAddressChange('country', e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="project-details-grid">
                        <div className="project-details-field full-width">
                          <label>ADDRESS:</label>
                          <span>{formatAddress(project.projectAddress)}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Office Address Section */}
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                      <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#48182F', margin: 0 }}>Office Address</h3>
                      {editingOfficeAddress ? (
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button className="btn btn-secondary btn-sm" onClick={handleCancelOfficeAddress}>
                            Cancel
                          </button>
                          <button className="btn btn-primary btn-sm" onClick={handleSaveOfficeAddress} disabled={savingOfficeAddress}>
                            {savingOfficeAddress ? 'Saving...' : 'Save'}
                          </button>
                        </div>
                      ) : (
                        <button className="project-details-edit-btn" onClick={handleEditOfficeAddress}>
                          <Pencil size={16} />
                        </button>
                      )}
                    </div>

                    {editingOfficeAddress ? (
                      <div className="project-details-edit-form">
                        <div className="project-details-form-group-full">
                          <label>STREET ADDRESS 1:</label>
                          <input
                            type="text"
                            className="form-input"
                            value={officeAddressData.street1}
                            onChange={(e) => handleOfficeAddressChange('street1', e.target.value)}
                          />
                        </div>
                        <div className="project-details-form-group-full">
                          <label>STREET ADDRESS 2:</label>
                          <input
                            type="text"
                            className="form-input"
                            value={officeAddressData.street2}
                            onChange={(e) => handleOfficeAddressChange('street2', e.target.value)}
                          />
                        </div>
                        <div className="project-details-form-row">
                          <div className="project-details-form-group">
                            <label>CITY:</label>
                            <input
                              type="text"
                              className="form-input"
                              value={officeAddressData.city}
                              onChange={(e) => handleOfficeAddressChange('city', e.target.value)}
                            />
                          </div>
                          <div className="project-details-form-group">
                            <label>STATE/PROVINCE:</label>
                            <input
                              type="text"
                              className="form-input"
                              value={officeAddressData.state}
                              onChange={(e) => handleOfficeAddressChange('state', e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="project-details-form-row">
                          <div className="project-details-form-group">
                            <label>ZIP/POSTAL CODE:</label>
                            <input
                              type="text"
                              className="form-input"
                              value={officeAddressData.zip}
                              onChange={(e) => handleOfficeAddressChange('zip', e.target.value)}
                            />
                          </div>
                          <div className="project-details-form-group">
                            <label>COUNTRY:</label>
                            <input
                              type="text"
                              className="form-input"
                              value={officeAddressData.country}
                              onChange={(e) => handleOfficeAddressChange('country', e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="project-details-grid">
                        <div className="project-details-field full-width">
                          <label>ADDRESS:</label>
                          <span>{formatAddress(project.officeAddress)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Description - Full Width */}
              <div style={{ padding: '0 40px', marginBottom: '24px' }}>
                <div className="project-details-card">
                <div className="project-details-card-header">
                  <h2>Description</h2>
                  {editingDescription ? (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button className="btn btn-secondary btn-sm" onClick={handleCancelDescription}>
                        Cancel
                      </button>
                      <button className="btn btn-primary btn-sm" onClick={handleSaveDescription} disabled={savingDescription}>
                        {savingDescription ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  ) : (
                    <button className="project-details-edit-btn" onClick={handleEditDescription}>
                      <Pencil size={16} />
                    </button>
                  )}
                </div>

                {editingDescription ? (
                  <div className="project-details-edit-form">
                    <RichTextEditor
                      content={descriptionData}
                      onChange={setDescriptionData}
                      placeholder="Enter project description..."
                    />
                  </div>
                ) : (
                  <div className="project-details-description">
                    <div dangerouslySetInnerHTML={{ __html: project.description || '<p>No description provided.</p>' }} />
                  </div>
                )}
              </div>
              </div>

              {/* Row for Intro Video (1/3) and Unit/Lot Information (2/3) */}
              <div className="project-details-row project-details-row-video-unit">
                {/* Intro Video */}
                <div className="project-details-card project-details-card-video">
                  <div className="project-details-card-header">
                    <h2>Intro Video</h2>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {project.introVideoUrl && (
                        <button
                          className="project-details-edit-btn"
                          onClick={handleRemoveVideo}
                          disabled={uploadingVideo}
                          title="Remove video"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                      <button
                        className="project-details-edit-btn"
                        onClick={() => setShowVideoModal(true)}
                        disabled={uploadingVideo}
                      >
                        <Pencil size={16} />
                      </button>
                    </div>
                  </div>
                  {project.introVideoUrl && project.introVideoType ? (
                    <div className="project-details-video-container">
                      <VideoPlayer
                        videoUrl={project.introVideoUrl}
                        videoType={project.introVideoType}
                      />
                    </div>
                  ) : (
                    <div className="project-details-video-placeholder" onClick={() => setShowVideoModal(true)} style={{ cursor: 'pointer' }}>
                      <div className="project-video-icon">
                        <Camera size={48} style={{ color: '#9CA3AF' }} />
                      </div>
                      <p>NO VIDEO AVAILABLE</p>
                      <p style={{ fontSize: '12px', marginTop: '8px', color: '#6B7280' }}>Click to add video</p>
                    </div>
                  )}
                </div>

                {/* Unit/Lot Information */}
                <div className="project-details-card project-details-card-unit">
                  <div className="project-details-card-header">
                    <h2>Unit/Lot Information</h2>
                    {editingUnitInfo ? (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button className="btn btn-secondary btn-sm" onClick={handleCancelUnitInfo}>
                          Cancel
                        </button>
                        <button className="btn btn-primary btn-sm" onClick={handleSaveUnitInfo} disabled={savingUnitInfo}>
                          {savingUnitInfo ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    ) : (
                      <button className="project-details-edit-btn" onClick={handleEditUnitInfo}>
                        <Pencil size={16} />
                      </button>
                    )}
                  </div>

                  {editingUnitInfo ? (
                    <div className="project-details-edit-form">
                      {unitInfoData.map((info, index) => (
                        <div key={index} style={{ marginBottom: '20px' }}>
                          <div style={{
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#374151',
                            marginBottom: '12px',
                            paddingBottom: '8px',
                            borderBottom: '1px solid #E5E7EB'
                          }}>
                            {info.type === 'condominium' && 'Condominium'}
                            {info.type === 'detached' && 'Detached Homes'}
                            {info.type === 'semi-detached' && 'Semi-Detached Homes'}
                            {info.type === 'townhouse' && 'Townhouses'}
                          </div>
                          <div className="project-details-form-row">
                            {info.type === 'condominium' && (
                              <>
                                <div className="project-details-form-group">
                                  <label>TOTAL UNITS/LOTS:</label>
                                  <input
                                    type="number"
                                    className="form-input"
                                    value={info.totalUnits || ''}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      setUnitInfoData(prev => {
                                        const updated = [...prev];
                                        updated[index] = { ...updated[index], totalUnits: value ? parseInt(value) : undefined };
                                        return updated;
                                      });
                                    }}
                                  />
                                </div>
                                <div className="project-details-form-group">
                                  <label>TOTAL FLOORS:</label>
                                  <input
                                    type="number"
                                    className="form-input"
                                    value={info.totalFloors || ''}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      setUnitInfoData(prev => {
                                        const updated = [...prev];
                                        updated[index] = { ...updated[index], totalFloors: value ? parseInt(value) : undefined };
                                        return updated;
                                      });
                                    }}
                                  />
                                </div>
                              </>
                            )}
                            {info.type === 'detached' && (
                              <div className="project-details-form-group">
                                <label>TOTAL UNITS/LOTS:</label>
                                <input
                                  type="number"
                                  className="form-input"
                                  value={info.totalUnits || ''}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setUnitInfoData(prev => {
                                      const updated = [...prev];
                                      updated[index] = { ...updated[index], totalUnits: value ? parseInt(value) : undefined };
                                      return updated;
                                    });
                                  }}
                                />
                              </div>
                            )}
                            {info.type === 'semi-detached' && (
                              <>
                                <div className="project-details-form-group">
                                  <label>TOTAL UNITS/LOTS:</label>
                                  <input
                                    type="number"
                                    className="form-input"
                                    value={info.totalUnits || ''}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      setUnitInfoData(prev => {
                                        const updated = [...prev];
                                        updated[index] = { ...updated[index], totalUnits: value ? parseInt(value) : undefined };
                                        return updated;
                                      });
                                    }}
                                  />
                                </div>
                                <div className="project-details-form-group">
                                  <label>TOTAL HOMES:</label>
                                  <input
                                    type="number"
                                    className="form-input"
                                    value={info.totalHomes || ''}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      setUnitInfoData(prev => {
                                        const updated = [...prev];
                                        updated[index] = { ...updated[index], totalHomes: value ? parseInt(value) : undefined };
                                        return updated;
                                      });
                                    }}
                                  />
                                </div>
                              </>
                            )}
                            {info.type === 'townhouse' && (
                              <>
                                <div className="project-details-form-group">
                                  <label>TOTAL BUILDINGS:</label>
                                  <input
                                    type="number"
                                    className="form-input"
                                    value={info.totalBuildings || ''}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      setUnitInfoData(prev => {
                                        const updated = [...prev];
                                        updated[index] = { ...updated[index], totalBuildings: value ? parseInt(value) : undefined };
                                        return updated;
                                      });
                                    }}
                                  />
                                </div>
                                <div className="project-details-form-group">
                                  <label>TOTAL HOMES:</label>
                                  <input
                                    type="number"
                                    className="form-input"
                                    value={info.totalHomes || ''}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      setUnitInfoData(prev => {
                                        const updated = [...prev];
                                        updated[index] = { ...updated[index], totalHomes: value ? parseInt(value) : undefined };
                                        return updated;
                                      });
                                    }}
                                  />
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div>
                      {project.additionalInfo && project.additionalInfo.length > 0 ? (
                        project.additionalInfo.map((info, index) => (
                          <div key={index} style={{ marginBottom: '16px' }}>
                            <div style={{
                              fontSize: '13px',
                              fontWeight: '600',
                              color: '#374151',
                              marginBottom: '12px',
                              paddingBottom: '8px',
                              borderBottom: '1px solid #E5E7EB'
                            }}>
                              {info.type === 'condominium' && 'Condominium'}
                              {info.type === 'detached' && 'Detached Homes'}
                              {info.type === 'semi-detached' && 'Semi-Detached Homes'}
                              {info.type === 'townhouse' && 'Townhouses'}
                            </div>
                            <div className="project-details-grid">
                              {info.type === 'condominium' && (
                                <>
                                  <div className="project-details-field">
                                    <label>TOTAL UNITS/LOTS:</label>
                                    <span>{info.totalUnits || '—'}</span>
                                  </div>
                                  <div className="project-details-field">
                                    <label>TOTAL FLOORS:</label>
                                    <span>{info.totalFloors || '—'}</span>
                                  </div>
                                </>
                              )}
                              {info.type === 'detached' && (
                                <div className="project-details-field">
                                  <label>TOTAL UNITS/LOTS:</label>
                                  <span>{info.totalUnits || '—'}</span>
                                </div>
                              )}
                              {info.type === 'semi-detached' && (
                                <>
                                  <div className="project-details-field">
                                    <label>TOTAL UNITS/LOTS:</label>
                                    <span>{info.totalUnits || '—'}</span>
                                  </div>
                                  <div className="project-details-field">
                                    <label>TOTAL HOMES:</label>
                                    <span>{info.totalHomes || '—'}</span>
                                  </div>
                                </>
                              )}
                              {info.type === 'townhouse' && (
                                <>
                                  <div className="project-details-field">
                                    <label>TOTAL BUILDINGS:</label>
                                    <span>{info.totalBuildings || '—'}</span>
                                  </div>
                                  <div className="project-details-field">
                                    <label>TOTAL HOMES:</label>
                                    <span>{info.totalHomes || '—'}</span>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        ))
                      ) : (
                        <p style={{ color: '#6B7280', fontStyle: 'italic' }}>No unit/lot information provided</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Row for Spaces, Levels, and Views with max height and scroll */}
              <div className="project-details-row project-details-row-scrollable">
                {/* Spaces */}
                <div className="project-details-card project-details-card-scrollable">
                  <div className="project-details-card-header">
                    <h2>Spaces</h2>
                    {editingSpaces ? (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button className="btn btn-secondary btn-sm" onClick={handleCancelSpaces}>
                          Cancel
                        </button>
                        <button className="btn btn-primary btn-sm" onClick={handleSaveSpaces} disabled={savingSpaces}>
                          {savingSpaces ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    ) : (
                      <button className="project-details-edit-btn" onClick={handleEditSpaces}>
                        <Pencil size={16} />
                      </button>
                    )}
                  </div>

                  {editingSpaces ? (
                    <>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => setShowAddSpacesModal(true)}
                        style={{ marginBottom: '12px' }}
                      >
                        + Add Spaces
                      </button>
                      <SortableList
                        items={spacesData}
                        onChange={handleSpacesChange}
                        placeholder="Add custom space name"
                        addButtonText="+"
                      />
                      <AddItemsModal
                        isOpen={showAddSpacesModal}
                        onClose={() => setShowAddSpacesModal(false)}
                        onAdd={handleAddSpacesFromModal}
                        availableOptions={[
                          '2nd Bedroom',
                          '3rd Bedroom',
                          '4th Bedroom',
                          'Balcony',
                          'Den',
                          'Dining Room',
                          'Ensuite Bathroom',
                          'Family Room',
                          'Foyer',
                          'Great Room',
                          'Kitchen',
                          'Laundry Room',
                          'Living Room',
                          'Loft',
                          'Main Bathroom',
                          'Master Bedroom',
                          'Powder Room',
                          'Primary Bedroom',
                          'Sitting Area',
                          'Solarium',
                          'Storage',
                          'Study',
                          'Terrace',
                          'Walk-in Closet'
                        ]}
                        title="Add Spaces"
                      />
                    </>
                  ) : (
                    <div className="project-details-list">
                      {project.spaces && project.spaces.length > 0 ? (
                        project.spaces.map((space, index) => (
                          <div key={index} className="project-details-list-item">
                            {space}
                          </div>
                        ))
                      ) : (
                        <p style={{ color: '#6B7280', fontStyle: 'italic' }}>No spaces defined</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Levels */}
                <div className="project-details-card project-details-card-scrollable">
                  <div className="project-details-card-header">
                    <h2>Levels</h2>
                    {editingLevels ? (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button className="btn btn-secondary btn-sm" onClick={handleCancelLevels}>
                          Cancel
                        </button>
                        <button className="btn btn-primary btn-sm" onClick={handleSaveLevels} disabled={savingLevels}>
                          {savingLevels ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    ) : (
                      <button className="project-details-edit-btn" onClick={handleEditLevels}>
                        <Pencil size={16} />
                      </button>
                    )}
                  </div>

                  {editingLevels ? (
                    <>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => setShowAddLevelsModal(true)}
                        style={{ marginBottom: '12px' }}
                      >
                        + Add Levels
                      </button>
                      <SortableList
                        items={levelsData}
                        onChange={handleLevelsChange}
                        placeholder="Add custom level name"
                        addButtonText="+"
                      />
                      <AddItemsModal
                        isOpen={showAddLevelsModal}
                        onClose={() => setShowAddLevelsModal(false)}
                        onAdd={handleAddLevelsFromModal}
                        availableOptions={[
                          'Ground Floor',
                          'Basement',
                          'Level 1',
                          'Level 2',
                          'Level 3',
                          'Level 4',
                          'Level 5',
                          'Level 6',
                          'Level 7',
                          'Level 8',
                          'Level 9',
                          'Level 10',
                          'Mezzanine',
                          'Penthouse',
                          'Rooftop',
                          'Parking Level P1',
                          'Parking Level P2',
                          'Parking Level P3',
                          'Parking Level P4',
                          'Parking Level P5'
                        ]}
                        title="Add Levels"
                      />
                    </>
                  ) : (
                    <div className="project-details-list">
                      {project.levels && project.levels.length > 0 ? (
                        project.levels.map((level, index) => (
                          <div key={index} className="project-details-list-item">
                            {level}
                          </div>
                        ))
                      ) : (
                        <p style={{ color: '#6B7280', fontStyle: 'italic' }}>No levels defined</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Views */}
                <div className="project-details-card project-details-card-scrollable">
                  <div className="project-details-card-header">
                    <h2>Views</h2>
                    {editingViews ? (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button className="btn btn-secondary btn-sm" onClick={handleCancelViews}>
                          Cancel
                        </button>
                        <button className="btn btn-primary btn-sm" onClick={handleSaveViews} disabled={savingViews}>
                          {savingViews ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    ) : (
                      <button className="project-details-edit-btn" onClick={handleEditViews}>
                        <Pencil size={16} />
                      </button>
                    )}
                  </div>

                  {editingViews ? (
                    <>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => setShowAddViewsModal(true)}
                        style={{ marginBottom: '12px' }}
                      >
                        + Add Views
                      </button>
                      <SortableList
                        items={viewsData}
                        onChange={handleViewsChange}
                        placeholder="Add custom view name"
                        addButtonText="+"
                      />
                      <AddItemsModal
                        isOpen={showAddViewsModal}
                        onClose={() => setShowAddViewsModal(false)}
                        onAdd={handleAddViewsFromModal}
                        availableOptions={[
                          'City View',
                          'Garden View',
                          'Lake View',
                          'Mountain View',
                          'Ocean View',
                          'Park View',
                          'Pool View',
                          'River View',
                          'Skyline View',
                          'Street View',
                          'Unobstructed View',
                          'Valley View',
                          'Water View'
                        ]}
                        title="Add Views"
                      />
                    </>
                  ) : (
                    <div className="project-details-list">
                      {project.views && project.views.length > 0 ? (
                        project.views.map((view, index) => (
                          <div key={index} className="project-details-list-item">
                            {view}
                          </div>
                        ))
                      ) : (
                        <p style={{ color: '#6B7280', fontStyle: 'italic' }}>No views defined</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* FEATURES TAB */}
          {activeTab === 'features' && (
            <div className="project-details-features">
              {/* Features Description */}
              <div className="project-details-card">
                <div className="project-details-card-header">
                  <h2>Features and Finishes</h2>
                  {editingFeatures ? (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button className="btn btn-secondary btn-sm" onClick={handleCancelFeatures}>
                        Cancel
                      </button>
                      <button className="btn btn-primary btn-sm" onClick={handleSaveFeatures} disabled={savingFeatures}>
                        {savingFeatures ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  ) : (
                    <button className="project-details-edit-btn" onClick={handleEditFeatures}>
                      <Pencil size={16} />
                    </button>
                  )}
                </div>

                {editingFeatures ? (
                  <div className="project-details-edit-form">
                    <RichTextEditor
                      content={featuresData}
                      onChange={setFeaturesData}
                      placeholder="Describe features and finishes..."
                    />
                  </div>
                ) : (
                  <div className="project-details-description">
                    <div dangerouslySetInnerHTML={{ __html: project.features || '<p>No features and finishes information provided.</p>' }} />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* AMENITIES TAB */}
          {activeTab === 'amenities' && (
            <div className="project-details-amenities">
              <div className="project-details-card">
                <div className="project-details-card-header">
                  <h2>Amenities</h2>
                  <button className="btn btn-primary btn-sm" onClick={handleCreateAmenity}>
                    + Add Amenity
                  </button>
                </div>

                {loadingAmenities ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                    <div className="projects-loading-spinner" />
                  </div>
                ) : amenities.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                    <p>No amenities added yet</p>
                  </div>
                ) : (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                    gap: '24px',
                    marginTop: '20px'
                  }}>
                    {amenities.map((amenity) => (
                      <AmenityCard
                        key={amenity._id}
                        amenity={amenity}
                        workspaceId={workspaceId!}
                        onClick={() => handleViewAmenity(amenity)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* DOCUMENTS TAB */}
          {activeTab === 'documents' && (
            <div className="project-details-documents">
              {/* Legal Documents */}
              <div className="project-details-card">
                <div className="project-details-card-header">
                  <h2>Legal Documents</h2>
                </div>
                <DocumentGallery
                  workspaceId={workspaceId!}
                  entityType="project"
                  entityId={projectId!}
                  fileType="legal_document"
                  acceptTypes=".doc,.docx"
                  helpText="All legal agreements and amendments can be uploaded here. Only Word Templates are accepted."
                />
              </div>

              {/* Marketing Documents */}
              <div className="project-details-card">
                <div className="project-details-card-header">
                  <h2>Marketing Documents</h2>
                </div>
                <DocumentGallery
                  workspaceId={workspaceId!}
                  entityType="project"
                  entityId={projectId!}
                  fileType="marketing_document"
                  acceptTypes=".pdf,.doc,.docx"
                  helpText="Please upload PDF or Word Templates"
                />
              </div>

              {/* Amendments to Track */}
              <div className="project-details-card">
                <div className="project-details-card-header">
                  <h2>Amendments to Track</h2>
                </div>
                <DocumentGallery
                  workspaceId={workspaceId!}
                  entityType="project"
                  entityId={projectId!}
                  fileType="amendments_track"
                  acceptTypes=".pdf,.doc,.docx"
                  helpText="Amendments can be used to track signed amendments for each deals."
                />
              </div>
            </div>
          )}

          {/* MEDIA TAB */}
          {activeTab === 'media' && (
            <div className="project-details-media">
              {/* Photo Gallery */}
              <div className="project-details-card">
                <div className="project-details-card-header">
                  <h2>Photo Gallery</h2>
                </div>
                <PhotoGallery
                  workspaceId={workspaceId!}
                  entityType="project"
                  entityId={projectId!}
                  imageType="photo"
                  allowMultiple={true}
                />
              </div>

              {/* Video Gallery */}
              <div className="project-details-card">
                <div className="project-details-card-header">
                  <h2>Video Gallery</h2>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => videoGalleryRef.current?.openAddDialog()}
                  >
                    <LinkIcon size={16} style={{ marginRight: '6px' }} />
                    Add Video
                  </button>
                </div>
                <VideoGallery
                  ref={videoGalleryRef}
                  workspaceId={workspaceId!}
                  entityType="project"
                  entityId={projectId!}
                  videos={videos}
                  onAddVideo={handleAddVideo}
                  onDeleteVideo={handleDeleteVideo}
                  onUpdateVideo={handleUpdateVideo}
                  hideHeader={true}
                />
              </div>
            </div>
          )}

          {/* TEMPLATES TAB */}
          {activeTab === 'templates' && (
            <div className="project-details-templates">
              <div className="project-details-card">
                <div className="project-details-card-header">
                  <h2>Document Templates</h2>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <a
                      href="/sample-template.html"
                      download="sample-template.html"
                      className="btn btn-secondary btn-sm"
                      style={{ textDecoration: 'none' }}
                    >
                      <Download size={16} style={{ marginRight: '6px' }} />
                      Download Sample
                    </a>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => setShowUploadTemplateModal(true)}
                    >
                      <Upload size={16} style={{ marginRight: '6px' }} />
                      Upload Template
                    </button>
                  </div>
                </div>

                {loadingTemplates ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                    <div className="builders-loading-spinner" />
                  </div>
                ) : templates.length === 0 ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '60px 20px',
                    background: '#F9FAFB',
                    borderRadius: '12px',
                    border: '1px dashed #D1D5DB'
                  }}>
                    <FileText size={48} color="#9CA3AF" style={{ marginBottom: '16px' }} />
                    <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                      No Templates Yet
                    </h3>
                    <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '16px' }}>
                      Upload your first template to start generating documents
                    </p>

                    {/* Sample Template Download */}
                    <div style={{
                      background: '#FDF2F8',
                      border: '1px solid #F9D7E8',
                      borderRadius: '8px',
                      padding: '16px',
                      marginBottom: '24px',
                      maxWidth: '500px',
                      marginLeft: 'auto',
                      marginRight: 'auto'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <Info size={18} style={{ color: '#48182F' }} />
                        <span style={{ fontSize: '14px', fontWeight: '600', color: '#48182F' }}>
                          Get Started with Sample Templates
                        </span>
                      </div>
                      <p style={{ fontSize: '13px', color: '#374151', marginBottom: '12px' }}>
                        Download our pre-built HTML template to get started quickly. Easy to customize!
                      </p>
                      <a
                        href={`${process.env.REACT_APP_API_URL?.replace('/api/v1', '') || 'http://localhost:5010'}/sample-templates/color-selection-amendment.html`}
                        download="color-selection-amendment.html"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          background: '#48182F',
                          color: 'white',
                          padding: '8px 16px',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: '500',
                          textDecoration: 'none',
                          transition: 'background 0.2s'
                        }}
                      >
                        <Download size={16} />
                        Download Sample Template
                      </a>
                    </div>
                  </div>
                ) : (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                    gap: '20px',
                    padding: '20px 0'
                  }}>
                    {templates.map((template) => (
                      <div
                        key={template._id}
                        style={{
                          background: '#FFFFFF',
                          border: '1px solid #D1D5DB',
                          borderRadius: '12px',
                          padding: '20px',
                          boxShadow: '0 2px 4px rgba(72, 24, 47, 0.08)',
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                      >
                        <div style={{ marginBottom: '12px', minHeight: '70px' }}>
                          <h3 style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#48182F',
                            margin: '0 0 8px 0'
                          }}>
                            {template.name}
                          </h3>
                          <span style={{
                            display: 'inline-block',
                            fontSize: '11px',
                            fontWeight: '600',
                            color: '#48182F',
                            background: 'rgba(72, 24, 47, 0.1)',
                            padding: '4px 8px',
                            borderRadius: '6px',
                            textTransform: 'uppercase'
                          }}>
                            {getTemplateTypeLabel(template.type)}
                          </span>
                          {template.description && (
                            <p style={{
                              fontSize: '13px',
                              color: '#6B7280',
                              margin: '8px 0 0 0'
                            }}>
                              {template.description}
                            </p>
                          )}
                        </div>

                        <div style={{
                          background: '#F9FAFB',
                          borderRadius: '8px',
                          padding: '12px',
                          marginBottom: '16px'
                        }}>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            fontSize: '12px',
                            color: '#6B7280',
                            marginBottom: '8px'
                          }}>
                            <span>File:</span>
                            <span style={{ fontWeight: '500', color: '#374151' }}>
                              {template.fileName}
                            </span>
                          </div>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            fontSize: '12px',
                            color: '#6B7280'
                          }}>
                            <span>Size:</span>
                            <span style={{ fontWeight: '500', color: '#374151' }}>
                              {formatFileSize(template.fileSize)}
                            </span>
                          </div>
                        </div>

                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: 'auto' }}>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => handleEditTemplate(template)}
                            style={{ flex: 1 }}
                          >
                            <Edit size={14} style={{ marginRight: '4px' }} />
                            Edit
                          </button>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => handleEditTemplateMetadata(template)}
                            title="Change name and type"
                          >
                            <Settings size={14} />
                          </button>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => handleDownloadTemplate(template)}
                          >
                            <Download size={14} />
                          </button>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDeleteTemplate(template._id)}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        title="Delete Project"
        message={`Are you sure you want to delete "${project.name}"? This action cannot be undone and will remove all associated data including photos and videos.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteProject}
        onClose={() => setShowDeleteDialog(false)}
        loading={deleting}
      />

      {/* Generic Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialogOpen}
        title={confirmDialogConfig.title}
        message={confirmDialogConfig.message}
        confirmText="Confirm"
        cancelText="Cancel"
        onConfirm={confirmDialogConfig.onConfirm}
        onClose={() => setConfirmDialogOpen(false)}
        loading={deletingTemplate}
      />


      {/* Amenity Form Modal */}
      <AmenityFormModal
        isOpen={amenityFormOpen}
        onClose={() => {
          setAmenityFormOpen(false);
          setEditingAmenity(null);
        }}
        onSave={handleSaveAmenity}
        amenity={editingAmenity}
        workspaceId={workspaceId!}
        projectId={projectId!}
      />

      {/* Amenity View Modal */}
      <AmenityViewModal
        isOpen={amenityViewOpen}
        onClose={() => {
          setAmenityViewOpen(false);
          setSelectedAmenity(null);
        }}
        amenity={selectedAmenity}
        workspaceId={workspaceId!}
        onEdit={handleEditAmenity}
        onDelete={handleDeleteAmenity}
      />

      {/* Upload Template Modal */}
      {showUploadTemplateModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '600px' }}>
            <div className="modal-header" style={{
              borderBottom: '1px solid #E5E7EB',
              paddingBottom: '16px'
            }}>
              <div>
                <h2 style={{ marginBottom: '4px', fontSize: '20px', fontWeight: '600', color: '#111827' }}>
                  Upload Template
                </h2>
                <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
                  Upload an HTML template for document generation
                </p>
              </div>
              <button
                className="modal-close-btn"
                onClick={() => setShowUploadTemplateModal(false)}
                style={{
                  background: '#F3F4F6',
                  borderRadius: '8px',
                  padding: '8px',
                  transition: 'background 0.2s'
                }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="modal-body" style={{ padding: '24px' }}>
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  Template Name <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={uploadTemplateForm.name}
                  onChange={(e) => setUploadTemplateForm({ ...uploadTemplateForm, name: e.target.value })}
                  placeholder="e.g., Standard Color Selection Amendment"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'border-color 0.2s, box-shadow 0.2s'
                  }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  Template Type <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <select
                  className="form-control"
                  value={uploadTemplateForm.type}
                  onChange={(e) => setUploadTemplateForm({ ...uploadTemplateForm, type: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'border-color 0.2s, box-shadow 0.2s',
                    background: 'white'
                  }}
                >
                  <option value="">Select type...</option>
                  {templateTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                {uploadTemplateForm.type && (
                  <button
                    style={{
                      marginTop: '10px',
                      fontSize: '13px',
                      color: '#48182F',
                      background: '#FDF2F8',
                      border: '1px solid #F9D7E8',
                      borderRadius: '6px',
                      padding: '6px 12px',
                      cursor: 'pointer',
                      fontWeight: '500',
                      transition: 'all 0.2s',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                    onClick={() => fetchPlaceholders(uploadTemplateForm.type)}
                  >
                    <Info size={14} />
                    View available placeholders
                  </button>
                )}
              </div>

              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  Description
                </label>
                <textarea
                  className="form-control"
                  value={uploadTemplateForm.description}
                  onChange={(e) => setUploadTemplateForm({ ...uploadTemplateForm, description: e.target.value })}
                  placeholder="Optional description..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'border-color 0.2s, box-shadow 0.2s',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: '0' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  Template File (.html) <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <div style={{
                  position: 'relative',
                  border: '2px dashed #D1D5DB',
                  borderRadius: '8px',
                  padding: '24px',
                  textAlign: 'center',
                  background: '#F9FAFB',
                  transition: 'all 0.2s'
                }}>
                  <input
                    type="file"
                    accept=".html"
                    onChange={handleTemplateFileChange}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      opacity: 0,
                      cursor: 'pointer'
                    }}
                  />
                  <Upload size={32} style={{ color: '#9CA3AF', marginBottom: '8px' }} />
                  <p style={{ fontSize: '14px', color: '#6B7280', margin: '0 0 4px 0' }}>
                    <span style={{ color: '#48182F', fontWeight: '500' }}>Click to upload</span> or drag and drop
                  </p>
                  <p style={{ fontSize: '12px', color: '#9CA3AF', margin: 0 }}>
                    HTML template file (.html)
                  </p>
                </div>
                {uploadTemplateForm.file && (
                  <div style={{
                    marginTop: '12px',
                    padding: '12px',
                    background: '#FDF2F8',
                    border: '1px solid #F9D7E8',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}>
                    <FileText size={18} style={{ color: '#48182F', flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{
                        fontSize: '13px',
                        color: '#48182F',
                        fontWeight: '500',
                        margin: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {uploadTemplateForm.file.name}
                      </p>
                      <p style={{ fontSize: '12px', color: '#9CA3AF', margin: 0 }}>
                        {formatFileSize(uploadTemplateForm.file.size)}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer" style={{
              borderTop: '1px solid #E5E7EB',
              paddingTop: '16px',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                className="btn btn-secondary btn-md"
                onClick={() => setShowUploadTemplateModal(false)}
                disabled={uploadingTemplate}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '500',
                  borderRadius: '8px',
                  transition: 'all 0.2s'
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary btn-md"
                onClick={handleUploadTemplate}
                disabled={uploadingTemplate || !uploadTemplateForm.name || !uploadTemplateForm.type || !uploadTemplateForm.file}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '500',
                  borderRadius: '8px',
                  transition: 'all 0.2s',
                  opacity: (uploadingTemplate || !uploadTemplateForm.name || !uploadTemplateForm.type || !uploadTemplateForm.file) ? 0.6 : 1
                }}
              >
                {uploadingTemplate ? 'Uploading...' : 'Upload Template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Placeholders Modal */}
      {showPlaceholdersModal && (
        <div className="modal-overlay" style={{ zIndex: 2000 }}>
          <div
            className="modal-content"
            style={{ maxWidth: '700px' }}
          >
            <div className="modal-header">
              <h2>Available Placeholders - {getTemplateTypeLabel(selectedTemplateType)}</h2>
              <button
                className="modal-close-btn"
                onClick={() => setShowPlaceholdersModal(false)}
              >
                <X size={20} />
              </button>
            </div>

            <div className="modal-body" style={{ overflowY: 'auto' }}>
              <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '20px' }}>
                Use these placeholders in your Word template. They will be replaced with actual data when generating documents.
              </p>

              {/* Loop Examples Section */}
              {placeholders.some(p => p.key.startsWith('#')) && (
                <div style={{
                  background: '#FDF2F8',
                  border: '1px solid #F9D7E8',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '20px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <Info size={18} style={{ color: '#48182F' }} />
                    <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#48182F' }}>
                      Loop Syntax Examples
                    </h3>
                  </div>
                  <div style={{ fontSize: '13px', color: '#374151' }}>
                    <p style={{ marginBottom: '12px' }}>
                      <strong>Handlebars Syntax Examples:</strong>
                    </p>
                    <div style={{
                      background: 'white',
                      border: '1px solid #E5E7EB',
                      borderRadius: '6px',
                      padding: '12px',
                      fontFamily: 'monospace',
                      fontSize: '12px',
                      marginBottom: '12px'
                    }}>
                      <div style={{ color: '#6B7280', marginBottom: '8px' }}>{'Example 1: Loop through purchasers'}</div>
                      <div style={{ color: '#48182F' }}>
                        {'{{#each purchasers}}'}<br />
                        &nbsp;&nbsp;{'<div>{{name}} - {{email}}</div>'}<br />
                        &nbsp;&nbsp;{'{{#if isPrimary}}<span>(Primary)</span>{{/if}}'}<br />
                        {'{{/each}}'}
                      </div>
                    </div>
                    <div style={{
                      background: 'white',
                      border: '1px solid #E5E7EB',
                      borderRadius: '6px',
                      padding: '12px',
                      fontFamily: 'monospace',
                      fontSize: '12px',
                      marginBottom: '12px'
                    }}>
                      <div style={{ color: '#6B7280', marginBottom: '8px' }}>{'Example 2: Conditional display'}</div>
                      <div style={{ color: '#48182F' }}>
                        {'{{#if projectLogoUrl}}'}<br />
                        &nbsp;&nbsp;{'<img src="{{projectLogoUrl}}" alt="Logo">'}<br />
                        {'{{/if}}'}
                      </div>
                    </div>
                    <p style={{ margin: 0, fontSize: '12px', color: '#6B7280' }}>
                      Use <code>{'{{#each array}}...{{/each}}'}</code> for loops and <code>{'{{#if condition}}...{{/if}}'}</code> for conditionals.
                    </p>
                  </div>
                </div>
              )}

              <table style={{ width: '100%', fontSize: '13px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #E5E7EB' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#48182F' }}>
                      Placeholder
                    </th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#48182F' }}>
                      Description
                    </th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#48182F' }}>
                      Example
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {placeholders.map((placeholder, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid #E5E7EB' }}>
                      <td style={{ padding: '12px', fontFamily: 'monospace', color: '#48182F', fontWeight: '500' }}>
                        {placeholder.key.startsWith('#')
                          ? `{{${placeholder.key}}}...{{/${placeholder.key.split(' ')[0].substring(1)}}}`
                          : `{{${placeholder.key}}}`
                        }
                      </td>
                      <td style={{ padding: '12px', color: '#374151' }}>
                        {placeholder.description}
                      </td>
                      <td style={{ padding: '12px', color: '#6B7280', fontStyle: 'italic' }}>
                        {placeholder.example}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-primary btn-md"
                onClick={() => setShowPlaceholdersModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Metadata Editor Modal */}
      {showTemplateMetadataModal && (
        <div className="modal-overlay">
          <div
            className="modal-content"
            style={{ maxWidth: '500px' }}
          >
            <div className="modal-header">
              <h2>Edit Template Settings</h2>
              <button
                className="modal-close-btn"
                onClick={() => setShowTemplateMetadataModal(false)}
              >
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <div style={{ marginBottom: '20px' }}>
                <label className="form-label">
                  Template Name <span style={{ color: '#DC2626' }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={templateMetadataForm.name}
                  onChange={(e) => setTemplateMetadataForm({ ...templateMetadataForm, name: e.target.value })}
                  placeholder="Enter template name"
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label className="form-label">
                  Template Type <span style={{ color: '#DC2626' }}>*</span>
                </label>
                <select
                  className="form-input"
                  value={templateMetadataForm.type}
                  onChange={(e) => setTemplateMetadataForm({ ...templateMetadataForm, type: e.target.value })}
                >
                  <option value="">Select type...</option>
                  {templateTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div style={{
                background: '#FEF3C7',
                border: '1px solid #FCD34D',
                borderRadius: '8px',
                padding: '12px',
                display: 'flex',
                alignItems: 'start',
                gap: '10px'
              }}>
                <Info size={16} style={{ color: '#92400E', flexShrink: 0, marginTop: '2px' }} />
                <div style={{ fontSize: '13px', color: '#92400E' }}>
                  <strong>Note:</strong> Changing the template type will affect which placeholders are available for this template.
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary btn-md"
                onClick={() => setShowTemplateMetadataModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary btn-md"
                onClick={handleSaveTemplateMetadata}
                disabled={!templateMetadataForm.name.trim() || !templateMetadataForm.type}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Editor Modal */}
      {showTemplateEditor && (
        <div className="modal-overlay">
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ width: '95vw', maxWidth: 'none', height: '98vh', maxHeight: 'none', display: 'flex', flexDirection: 'column', background: '#1E1E1E' }}
          >
            <div className="modal-header" style={{ background: '#FFFFFF', borderBottom: '1px solid #E5E7EB' }}>
              <div>
                <h2 style={{ color: '#111827' }}>Edit Template: {editingTemplate?.name}</h2>
                <p style={{ fontSize: '13px', color: '#6B7280', margin: '4px 0 0 0' }}>
                  {editingTemplate?.fileName} ({getTemplateTypeLabel(editingTemplate?.type)})
                </p>
              </div>
              <button
                className="modal-close-btn"
                onClick={() => {
                  if (!savingTemplate) {
                    setShowTemplateEditor(false);
                    setEditingTemplate(null);
                    setTemplateContent('');
                  }
                }}
                disabled={savingTemplate}
                style={{ color: '#6B7280' }}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ flex: 1, overflow: 'hidden', padding: '20px', paddingTop: '10px', position: 'relative' }}>
              {loadingTemplateContent && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(255, 255, 255, 0.95)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1000,
                  gap: '20px'
                }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    border: '4px solid #F3F4F6',
                    borderTop: '4px solid #48182F',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#48182F'
                  }}>
                    Loading template content...
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: '#6B7280'
                  }}>
                    Please wait while we fetch the template
                  </div>
                </div>
              )}

              <div style={{
                background: '#3E2C00',
                border: '1px solid #5C4A1F',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '15px',
                display: 'flex',
                alignItems: 'start',
                gap: '10px'
              }}>
                <Info size={18} style={{ color: '#FCD34D', flexShrink: 0, marginTop: '2px' }} />
                <div style={{ fontSize: '13px', color: '#FEF3C7' }}>
                  <strong>HTML Template with Handlebars Syntax:</strong> Use placeholders like <code style={{ background: '#2D2D30', padding: '2px 4px', borderRadius: '3px', color: '#CE9178' }}>{'{{projectName}}'}</code>,
                  <code style={{ background: '#2D2D30', padding: '2px 4px', borderRadius: '3px', color: '#CE9178' }}>{'{{#each purchasers}}...{{/each}}'}</code> for loops, and <code style={{ background: '#2D2D30', padding: '2px 4px', borderRadius: '3px', color: '#CE9178' }}>{'{{#if condition}}...{{/if}}'}</code> for conditionals.
                  View the <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      fetchPlaceholders(editingTemplate?.type);
                    }}
                    style={{ color: '#4FC3F7', textDecoration: 'underline' }}
                  >
                    available placeholders
                  </a> for this template type.
                </div>
              </div>

              <div style={{
                height: 'calc(100% - 70px)',
                border: '1px solid #1E1E1E',
                borderRadius: '8px',
                overflow: 'hidden',
                background: '#1E1E1E'
              }}>
                <Editor
                  height="100%"
                  defaultLanguage="html"
                  value={templateContent}
                  onChange={(value) => setTemplateContent(value || '')}
                  theme="vs-dark"
                  loading={
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      flexDirection: 'column',
                      gap: '15px'
                    }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        border: '3px solid #F3F4F6',
                        borderTop: '3px solid #48182F',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                      <div style={{ fontSize: '14px', color: '#6B7280' }}>
                        Initializing editor...
                      </div>
                    </div>
                  }
                  options={{
                    minimap: { enabled: true },
                    fontSize: 14,
                    wordWrap: 'on',
                    formatOnPaste: true,
                    formatOnType: true,
                    scrollBeyondLastLine: false,
                    automaticLayout: true,
                    readOnly: loadingTemplateContent
                  }}
                />
              </div>
            </div>

            <div className="modal-footer" style={{ background: '#FFFFFF', borderTop: '1px solid #E5E7EB' }}>
              <button
                className="btn btn-secondary btn-md"
                onClick={() => {
                  setShowTemplateEditor(false);
                  setEditingTemplate(null);
                  setTemplateContent('');
                }}
                disabled={savingTemplate || loadingTemplateContent}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary btn-md"
                onClick={handleSaveTemplate}
                disabled={savingTemplate || loadingTemplateContent || !templateContent}
                style={{
                  opacity: (savingTemplate || loadingTemplateContent || !templateContent) ? 0.6 : 1
                }}
              >
                {savingTemplate ? 'Saving...' : 'Save Template'}
              </button>
            </div>

            {/* Saving Overlay */}
            {savingTemplate && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(255, 255, 255, 0.95)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2000,
                gap: '20px',
                borderRadius: '12px'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  border: '4px solid #F3F4F6',
                  borderTop: '4px solid #48182F',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                <div style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#48182F'
                }}>
                  Saving Template...
                </div>
                <div style={{
                  fontSize: '14px',
                  color: '#6B7280',
                  textAlign: 'center',
                  maxWidth: '300px'
                }}>
                  Please wait while we save your changes to the server
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Video Upload Modal */}
      <VideoUploadModal
        isOpen={showVideoModal}
        onClose={() => setShowVideoModal(false)}
        onVideoUploaded={handleVideoUploaded}
        workspaceId={workspaceId!}
        projectId={projectId!}
        currentVideoUrl={project?.introVideoUrl}
      />

      {/* Change History FAB Button */}
      <ChangeHistoryButton
        onClick={() => setHistoryDrawerOpen(true)}
        isOpen={historyDrawerOpen}
      />

      {/* Change History Drawer */}
      <ChangeHistoryDrawer
        isOpen={historyDrawerOpen}
        onClose={() => setHistoryDrawerOpen(false)}
        workspaceId={workspaceId!}
        entityType="project"
        entityId={projectId!}
      />
    </AppLayout>
  );
};

export default ProjectDetails;
